// ============================================================================
// sirQuotaClient.js — client half of the free / OTP / credit metering.
//
// This file supplies IDENTITY and renders what the server tells it. It does NOT
// count anything: every "have you got a search left" decision is made by the
// backend inside the search handlers themselves (api_files/sirQuota.js), so
// editing localStorage, calling the API directly or opening a private window
// buys nobody an extra search.
//
// Tiers, as the server enforces them:
//   free    5 voters, no login   — metered per device fingerprint AND per IP
//   otp     5 more               — after WhatsApp OTP, metered per mobile number
//   credit  1 credit per voter   — purchased
//
// The billed unit is ONE VOTER OPENED, not one keystroke: the results list is
// free (people must be able to find their own name before being asked to pay),
// and opening the same voter twice is charged once, ever.
// ============================================================================

import { FetchFromServer } from "../../api/ServerApi";
import { getDeviceHash, getDeviceMeta } from "../../utils/deviceFingerprint";

/** Where the signed "this mobile is verified" token lives between visits. */
const TOKEN_KEY = "SirToken";
const MOBILE_KEY = "SirMobile";

const api = (name, payload) =>
  FetchFromServer(name, payload, { plain: true, open: true });

/* ------------------------------- session ---------------------------------- */

export const getSirToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

export const getSirMobile = () => {
  try {
    return localStorage.getItem(MOBILE_KEY) || "";
  } catch {
    return "";
  }
};

export const setSirSession = (token, mobile) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (mobile) localStorage.setItem(MOBILE_KEY, mobile);
  } catch {
    // Private mode with storage disabled: the session lasts this page load
    // only, which is inconvenient but not a security problem — the token is
    // re-issuable with another OTP.
  }
};

export const clearSirSession = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(MOBILE_KEY);
  } catch {
    /* nothing to clear */
  }
};

/* ------------------------------- identity --------------------------------- */

/** UserId of the karyakarta logged into the campaign app, 0 when nobody is. */
const getLoggedInUserId = () => {
  try {
    return Number(localStorage.getItem("UserId")) || 0;
  } catch {
    return 0;
  }
};

/**
 * Adds the identity fields every metered call needs. Always await this rather
 * than building a payload by hand — a request without a DeviceHash is refused
 * by the server, because an unmeterable caller cannot be given free searches.
 *
 * UserId is part of identity, not an optional extra: the server grants
 * UserId > 0 the 'internal' tier (unlimited, unmetered) because a logged-in
 * karyakarta is a paying customer, not a visitor. Omit it and someone who has
 * already logged in is counted as anonymous and walled behind an OTP they have
 * no reason to be asked for.
 */
export async function withIdentity(payload = {}) {
  const DeviceHash = await getDeviceHash();
  const meta = getDeviceMeta();
  // An explicit UserId in the payload wins; otherwise take the logged-in one.
  const UserId = payload.UserId != null ? payload.UserId : getLoggedInUserId();

  return {
    ...payload,
    // Sent only when there really is one — UserId: 0 would be a claim of
    // identity rather than the absence of one.
    ...(UserId ? { UserId } : {}),
    DeviceHash,
    SirToken: getSirToken(),
    Platform: meta.Platform,
    Screen: meta.Screen,
  };
}

/* --------------------------------- calls ---------------------------------- */

/** Current allowance. Safe to call often; it charges nothing. */
export async function fetchQuota() {
  const body = await withIdentity({});
  const res = await api("GetSirQuotaStatus", body);
  return res && res.quota ? res.quota : null;
}

export async function sendSirOtp(mobileNumber) {
  const body = await withIdentity({ MobileNumber: mobileNumber });
  return api("SendSirOtp", body);
}

/**
 * On success the signed token is stored, which is what upgrades the tier.
 *
 * `name` is optional and is recorded against the number, so an admin taking a
 * phone call can find the caller instead of staring at device hashes. It never
 * gates verification - a blank name still verifies.
 */
export async function verifySirOtp(mobileNumber, otp, name) {
  const body = await withIdentity({
    MobileNumber: mobileNumber,
    Otp: otp,
    Name: name || "",
  });
  const res = await api("VerifySirOtp", body);
  if (res && res.status === "ok" && res.SirToken) {
    setSirSession(res.SirToken, res.MobileNumber || mobileNumber);
  }
  return res;
}

export async function fetchCreditPlans() {
  return api("GetSirCreditPlans", await withIdentity({}));
}

export async function fetchCredits() {
  return api("GetSirCredits", await withIdentity({}));
}

/**
 * Everything this number has searched and opened, newest first.
 *
 * With no argument it returns the caller's OWN history, proved by the signed
 * token. A super admin may pass someone else's number; for anyone else the
 * server ignores it and answers "Not allowed", so a stranger cannot read a
 * history by guessing a mobile number.
 */
export async function fetchSearchHistory(mobileNumber, limit) {
  const payload = {};
  if (mobileNumber) payload.MobileNumber = mobileNumber;
  if (limit) payload.limit = limit;
  return api("GetSirSearchHistory", await withIdentity(payload));
}

/* -------------------------------- responses -------------------------------- */

/**
 * True when a response is the server refusing on quota grounds rather than
 * returning data. The metered handlers answer HTTP 200 with
 * `{status:'quota', reason}` — consistent with the rest of this API, which
 * never uses status codes for business outcomes.
 */
export const isQuotaBlock = (res) =>
  !!res && !Array.isArray(res) && res.status === "quota";

/**
 * Human wording for a block. `exhaustedBy: 'ip'` matters: in an office or a
 * college many people share one connection, and "this device" would read as a
 * bug to someone on their first visit.
 */
export function quotaBlockMessage(res) {
  const reason = res?.reason;
  const q = res?.quota;

  if (reason === "needCredit") {
    return "You have used all your free and verified searches. Buy credits to keep searching.";
  }
  if (reason === "needOtp") {
    return q?.exhaustedBy === "ip"
      ? "The 5 free searches for this internet connection are used up. Verify your mobile number to get 5 more of your own."
      : "You have used your 5 free searches. Verify your mobile number to get 5 more.";
  }
  if (reason === "rateLimit") {
    return "You have made a lot of searches today. Please try again tomorrow.";
  }
  if (reason === "noDevice") {
    return "We could not identify this browser. Please turn on cookies and site data, then reload the page.";
  }
  return res?.message || "This search could not be completed.";
}

/** Short line for the always-visible allowance badge. */
export function quotaSummary(q) {
  if (!q) return "";
  if (q.unlimited) return "Unlimited";
  if (q.tier === "credit" || (q.verified && q.otpRemaining === 0)) {
    return `${q.credits || 0} credit${q.credits === 1 ? "" : "s"} left`;
  }
  if (q.verified) {
    return `${q.otpRemaining} of ${q.otpLimit} verified searches left`;
  }
  return `${q.freeRemaining} of ${q.freeLimit} free searches left`;
}
